import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Edit, Clock, X, Loader2, Calendar } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'

interface Turno {
  id: number
  codigo: string
  nome: string
  hora_inicio: string  // HH:MM
  hora_fim: string
  ativo: boolean
}

export default function IndTurnosPage() {
  const qc = useQueryClient()
  const [modal, setModal] = useState<Turno | null | 'new'>(null)

  const { data: turnos = [], isLoading } = useQuery<Turno[]>({
    queryKey: ['ind-turnos'],
    queryFn: async () => (await api.get('/industrial/turnos')).data,
  })

  const { data: empresa } = useQuery<{ dias_uteis_semana: number }>({
    queryKey: ['empresa-atual-dus'],
    queryFn: async () => (await api.get('/empresa/atual')).data,
  })

  const horasDia = turnos.filter(t => t.ativo).reduce((sum, t) => {
    const [hi, mi] = t.hora_inicio.split(':').map(Number)
    const [hf, mf] = t.hora_fim.split(':').map(Number)
    let dur = (hf + mf / 60) - (hi + mi / 60)
    if (dur < 0) dur += 24
    return sum + dur
  }, 0)

  const updDus = useMutation({
    mutationFn: async (n: number) => (await api.put('/empresa/atual', { dias_uteis_semana: n })).data,
    onSuccess: () => {
      toast.success('Configuração salva')
      qc.invalidateQueries({ queryKey: ['empresa-atual-dus'] })
      qc.invalidateQueries({ queryKey: ['empresa-atual'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Clock className="h-6 w-6 text-violet-600" /> Turnos de Produção
        </h1>
        <button
          onClick={() => setModal('new')}
          className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg flex items-center gap-2 text-sm font-medium"
        >
          <Plus size={16} /> Novo turno
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KPI label="Horas/dia (turnos ativos)" valor={`${horasDia.toFixed(1)}h`} cor="violet" />
        <KPI label="Turnos cadastrados" valor={String(turnos.length)} cor="blue" />
        <div className="bg-white border border-ink-200 rounded-lg p-4">
          <div className="text-xs uppercase text-ink-500 font-semibold mb-2 flex items-center gap-1">
            <Calendar className="h-3 w-3" /> Dias úteis por semana
          </div>
          <div className="flex items-center gap-2">
            <select
              value={empresa?.dias_uteis_semana || 6}
              onChange={(e) => updDus.mutate(Number(e.target.value))}
              className="flex-1 h-10 border-2 border-ink-200 rounded-lg px-3 text-sm font-medium"
            >
              <option value={5}>5 (segunda a sexta)</option>
              <option value={6}>6 (segunda a sábado)</option>
              <option value={7}>7 (todos os dias)</option>
            </select>
          </div>
          <div className="text-[10px] text-ink-500 mt-1">Usado no cálculo de capacidade dia/semana/mês</div>
        </div>
      </div>

      <div className="bg-white border border-ink-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ink-50">
            <tr>
              <th className="text-left text-xs uppercase text-ink-600 px-3 py-2">Código</th>
              <th className="text-left text-xs uppercase text-ink-600 px-3 py-2">Nome</th>
              <th className="text-center text-xs uppercase text-ink-600 px-3 py-2">Início</th>
              <th className="text-center text-xs uppercase text-ink-600 px-3 py-2">Fim</th>
              <th className="text-right text-xs uppercase text-ink-600 px-3 py-2">Duração</th>
              <th className="text-center text-xs uppercase text-ink-600 px-3 py-2">Ativo</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {isLoading && <tr><td colSpan={7} className="text-center py-6 text-ink-500">Carregando…</td></tr>}
            {!isLoading && turnos.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-ink-500">
                Sem turnos. Crie pelo menos 1 pra capacidade calcular.
              </td></tr>
            )}
            {turnos.map(t => {
              const [hi, mi] = t.hora_inicio.split(':').map(Number)
              const [hf, mf] = t.hora_fim.split(':').map(Number)
              let dur = (hf + mf / 60) - (hi + mi / 60)
              if (dur < 0) dur += 24
              return (
                <tr key={t.id} onClick={() => setModal(t)} className="hover:bg-violet-50 cursor-pointer">
                  <td className="px-3 py-2.5 font-mono text-xs">{t.codigo}</td>
                  <td className="px-3 py-2.5 font-medium">{t.nome}</td>
                  <td className="px-3 py-2.5 text-center font-mono">{t.hora_inicio}</td>
                  <td className="px-3 py-2.5 text-center font-mono">{t.hora_fim}</td>
                  <td className="px-3 py-2.5 text-right font-medium">{dur.toFixed(1)}h</td>
                  <td className="px-3 py-2.5 text-center">
                    <span className={`text-[10px] px-2 py-0.5 rounded ${t.ativo ? 'bg-emerald-100 text-emerald-800' : 'bg-ink-200 text-ink-600'}`}>
                      {t.ativo ? 'ATIVO' : 'INATIVO'}
                    </span>
                  </td>
                  <td className="px-3 py-2.5"><Edit size={14} className="text-ink-400" /></td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {modal && (
        <TurnoModal
          turno={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSuccess={() => { qc.invalidateQueries({ queryKey: ['ind-turnos'] }); setModal(null) }}
        />
      )}
    </div>
  )
}


function KPI({ label, valor, cor }: { label: string; valor: string; cor: string }) {
  const cores: Record<string, string> = {
    violet: 'from-violet-50 to-violet-100 border-violet-200 text-violet-700',
    blue: 'from-blue-50 to-blue-100 border-blue-200 text-blue-700',
  }
  return (
    <div className={`bg-gradient-to-br border rounded-lg p-4 ${cores[cor]}`}>
      <div className="text-xs uppercase font-semibold">{label}</div>
      <div className="text-2xl font-bold mt-1">{valor}</div>
    </div>
  )
}


function TurnoModal({ turno, onClose, onSuccess }: { turno: Turno | null; onClose: () => void; onSuccess: () => void }) {
  const [form, setForm] = useState({
    codigo: turno?.codigo || '',
    nome: turno?.nome || '',
    hora_inicio: turno?.hora_inicio || '06:00',
    hora_fim: turno?.hora_fim || '14:00',
    ativo: turno?.ativo ?? true,
  })

  const save = useMutation({
    mutationFn: async () => {
      if (turno) return (await api.put(`/industrial/turnos/${turno.id}`, form)).data
      return (await api.post('/industrial/turnos', form)).data
    },
    onSuccess: () => { toast.success('Salvo'); onSuccess() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{turno ? 'Editar turno' : 'Novo turno'}</h2>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase text-ink-500 font-semibold">Código</label>
            <input value={form.codigo} onChange={e => setForm({ ...form, codigo: e.target.value })}
              placeholder="MANHA" className="w-full mt-1 px-3 py-2 border border-ink-300 rounded text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase text-ink-500 font-semibold">Nome</label>
            <input value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })}
              placeholder="Manhã" className="w-full mt-1 px-3 py-2 border border-ink-300 rounded text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase text-ink-500 font-semibold">Início</label>
            <input type="time" value={form.hora_inicio} onChange={e => setForm({ ...form, hora_inicio: e.target.value })}
              className="w-full mt-1 px-3 py-2 border border-ink-300 rounded text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase text-ink-500 font-semibold">Fim</label>
            <input type="time" value={form.hora_fim} onChange={e => setForm({ ...form, hora_fim: e.target.value })}
              className="w-full mt-1 px-3 py-2 border border-ink-300 rounded text-sm" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.ativo} onChange={e => setForm({ ...form, ativo: e.target.checked })} />
          Ativo (computa em capacidade)
        </label>
        <div className="flex justify-end gap-2 pt-3 border-t">
          <button onClick={onClose} className="px-3 py-2 border border-ink-300 rounded text-sm">Cancelar</button>
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending || !form.codigo || !form.nome}
            className="px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-40 text-white rounded text-sm font-medium flex items-center gap-1"
          >
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Salvar
          </button>
        </div>
      </div>
    </div>
  )
}
